import { Box, FormControl, Select, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { OverlayMenuItem } from './visualization-select-option';
import { useCallback, useMemo, useRef, useState } from 'react';
import { primary } from '../../../../constants/theme/colors.tsx';

interface VisualizationSelectProps {
  setSelectedOverlay: (val: string) => void;
  setMapViewType: (val: string) => void;
  selectedOverlay: string;
}

export const VisualizationSelect = ({
  setSelectedOverlay,
  setMapViewType,
  selectedOverlay,
}: VisualizationSelectProps) => {
  const [openOptions, setOpenOptions] = useState(false);
  const [hoveredOption, setHoveredOption] = useState('');
  const hoverTimeout = useRef(null as null | NodeJS.Timeout);

  const handleOpenOptions = useCallback(() => {
    setOpenOptions(true);
  }, []);

  const handleCloseOptions = useCallback(() => {
    setOpenOptions(false);
  }, []);

  const changeOverlay = useCallback(
    (val: string) => {
      setSelectedOverlay(val);
      if (val === 'Home Placement') {
        setMapViewType('map');
      }
    },
    [setMapViewType, setSelectedOverlay]
  );

  const handleHover = useCallback((hoveredOption: string) => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }

    hoverTimeout.current = setTimeout(() => {
      setHoveredOption(hoveredOption);
    }, 1000);
  }, []);

  const tipModalBody = useMemo(() => {
    switch (hoveredOption) {
      case 'Zoning':
        return `Zoning is a set of local laws or regulations that control how land can be used in specific areas. Zoning laws can apply to homes, businesses, and other properties, and can restrict what can be built or developed in a given area.`;
      case 'Setbacks':
        return `Setbacks are distance requirements in zoning laws that determine how far a building must be from a property line or street. Setbacks are measured in feet and can apply to the front, side, and rear of a structure. They can also include driveway areas and other surface improvements.`;
      case 'Topography':
        return `Topography is the study of the land's surface, including its features, elevation changes, and landscape. It can also refer to a depiction of the land's features on a map. In real estate, topography is important for property development and can influence the design, construction, and maintenance of buildings and infrastructure.`;
      case 'Flood Zone':
        return `A flood zone is a land area that's at risk of flooding from any source, such as a river or stream that breaches its banks. The Federal Emergency Management Agency (FEMA) defines a floodplain as an area that has a 1% chance of flooding beyond 6 inches or reaching base flood elevations in any given year.`;
      case 'Wetlands':
        return 'Wetlands are areas where water covers the soil, or is present either at or near the surface of the soil all year or for varying periods of time during the year, including during the growing season.';
      case 'Home Placement':
        return 'Home placement allows you to see setbacks, flood zones and wetlands all overlayed simultaneously on your lot. It also allows you to find the optimal home build location by placing it on the lot.';
      case 'Builder View':
        return 'Builder view allows you to see setbacks, flood zones and wetlands all overlayed simultaneously on your lot. It also allows you to find the optimal home build location by placing it on the lot.';
    }
  }, [hoveredOption]);

  const handleCloseModal = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    setHoveredOption('');
  }, []);

  return (
    <>
      <FormControl fullWidth size="small" style={{ backgroundColor: 'white' }}>
        <Select
          variant="standard"
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={selectedOverlay}
          displayEmpty
          open={openOptions}
          onOpen={handleOpenOptions}
          onClose={handleCloseOptions}
        >
          {OverlayMenuItem({
            label: 'None',
            onMouseEnter: handleHover,
            onMouseLeave: handleCloseModal,
            setSelectedOverlay: changeOverlay,
            isMobile: isMobile,
          })}
          {OverlayMenuItem({
            label: 'Setbacks',
            onMouseEnter: handleHover,
            onMouseLeave: handleCloseModal,
            setSelectedOverlay: changeOverlay,
            isMobile: isMobile,
          })}
          {OverlayMenuItem({
            label: 'Topography',
            onMouseEnter: handleHover,
            onMouseLeave: handleCloseModal,
            setSelectedOverlay: changeOverlay,
            isMobile: isMobile,
          })}
          {OverlayMenuItem({
            label: 'Flood Zone',
            onMouseEnter: handleHover,
            onMouseLeave: handleCloseModal,
            setSelectedOverlay: changeOverlay,
            isMobile: isMobile,
          })}
          {OverlayMenuItem({
            label: 'Wetlands',
            onMouseEnter: handleHover,
            onMouseLeave: handleCloseModal,
            setSelectedOverlay: changeOverlay,
            isMobile: isMobile,
          })}
          {OverlayMenuItem({
            label: 'Home Placement',
            onMouseEnter: handleHover,
            onMouseLeave: handleCloseModal,
            setSelectedOverlay: changeOverlay,
            isMobile: isMobile,
          })}
          {OverlayMenuItem({
            label: 'Builder View',
            onMouseEnter: handleHover,
            onMouseLeave: handleCloseModal,
            setSelectedOverlay: changeOverlay,
            isMobile: isMobile,
          })}
        </Select>{' '}
      </FormControl>

      {hoveredOption && openOptions && (
        <Box
          sx={{
            position: 'absolute',
            top: 57,
            right: 210,
            width: 300,
            bgcolor: 'background.paper',
            padding: 2,
            borderRadius: 0,
            borderColor: '#9E9E9E',
            border: '0.33px solid',
            zIndex: 10,
          }}
        >
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', color: primary.main }}
          >
            {hoveredOption}
          </Typography>
          <Typography
            sx={{
              mt: 2,
              fontSize: '0.75rem',
              textAlign: 'center',
              fontWeight: '100',
            }}
          >
            {tipModalBody}
          </Typography>
        </Box>
      )}
    </>
  );
};
